// src/common/components/cartModal/components/processingStage/hooks/useProcessingStage.ts

import { useState, useEffect, useCallback, useRef } from "react";
import useInterval from "@use-it/interval";
import { getApi } from "../../../../api/api";
import { PaymentStatus } from "../../../types/enums";

interface UseProcessingStageProps {
  orderId: number | null;
  timeout?: number; // мс, через сколько показывать таймаут
  intervalTime?: number; // интервал опроса
  onPaid?: () => void;
  onTimeout?: () => void;
}

export const useProcessingStage = ({
  orderId,
  timeout = 20000,
  intervalTime = 3000,
  onPaid,
  onTimeout,
}: UseProcessingStageProps) => {
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [startTime] = useState(Date.now());
  const [isPolling, setIsPolling] = useState(!!orderId);
  const api = getApi();

  // Сбрасываем таймаут при новом заказе
  useEffect(() => {
    if (orderId) {
      setIsTimedOut(false);
      setIsPolling(true);
    }
  }, [orderId]);

  // Проверка таймаута
  useEffect(() => {
    if (!isPolling || isTimedOut) return;

    const timer = setTimeout(() => {
      setIsTimedOut(true);
      setIsPolling(false);
      onTimeout?.();
    }, timeout);

    return () => clearTimeout(timer);
  }, [isPolling, isTimedOut, timeout, onTimeout]);

  // Опрос статуса
  useInterval(async () => {
    if (!orderId || !isPolling || isTimedOut) return;

    try {
      const order = await api.getOrder(orderId);

      if (order?.paymentStatus === PaymentStatus.PAID) {
        setIsPolling(false);
        onPaid?.();
      }
    } catch (error) {
      console.error("Payment polling error:", error);
    }
  }, intervalTime);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
  }, []);

  return {
    isTimedOut,
    isPolling,
    stopPolling,
  };
};
